'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ComboboxItem } from '@sola/ark-core/molecules/combobox-field/combobox-field.tsx';
import { ComboboxField } from '@sola/ark-core/molecules/combobox-field/combobox-field.tsx';
import { formatPostalCodeLabel } from '@sola/skippy-search-sdk/lib/geo.ts';
import { fetchAutocompletePostalCode, useAutocompletePostalCodeQuery } from '@sola/skippy-search-sdk/lib/queries.ts';
import type { GeoLocationDto } from '@sola/skippy-search-sdk/lib/schema.ts';
export interface GeoLookupFieldProps {
  /** Current display value for the input. */
  value?: string;
  /** Called when a location is selected or resolved. */
  onGeoChange?: (geo?: GeoLocationDto) => void;
  /** Called when the display value changes (for controlled input). */
  onValueChange?: (value: string) => void;
  /** Called when user submits (enter/blur) with the raw value. */
  onSubmit?: (value: string) => void;
  /** Placeholder text. */
  placeholder?: string;
  /** aria-label. */
  'aria-label'?: string;
  /** Additional class name. */
  className?: string;
}

/**
 * GeoLookupField — a reusable location lookup field with postal code autocomplete
 * and browser geolocation support.
 */
export const GeoLookupField: React.FC<GeoLookupFieldProps> = t0 => {
  const $ = _c(46);
  const {
    value: controlledValue,
    onGeoChange,
    onValueChange,
    onSubmit,
    placeholder,
    "aria-label": ariaLabel,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["volunteers"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const [locationSearch, setLocationSearch] = useState("");
  const t2 = locationSearch.length >= 3;
  let t3;
  if ($[1] !== t2) {
    t3 = {
      enabled: t2,
      limit: 5
    };
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: autocompleteResults,
    isFetching: isLoading
  } = useAutocompletePostalCodeQuery(locationSearch, t3);
  let t4;
  if ($[3] !== autocompleteResults) {
    t4 = autocompleteResults ?? [];
    $[3] = autocompleteResults;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4) {
    t5 = t4.map(_temp);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const suggestions = t5;
  let t6;
  if ($[7] !== onGeoChange || $[8] !== onValueChange || $[9] !== t) {
    t6 = () => {
      if (!navigator.geolocation) {
        return;
      }
      navigator.geolocation.getCurrentPosition(position => {
        const {
          latitude,
          longitude
        } = position.coords;
        onValueChange?.(t("volunteers:myLocation"));
        onGeoChange?.({
          latitude,
          longitude
        });
      });
    };
    $[7] = onGeoChange;
    $[8] = onValueChange;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const requestGeolocation = t6;
  let t7;
  if ($[11] !== onGeoChange || $[12] !== onValueChange) {
    t7 = item => {
      const displayValue = item.data?.city && item.data?.state ? `${item.value} - ${item.data.city}, ${item.data.state}` : item.label;
      onValueChange?.(displayValue);
      setLocationSearch("");
      if (item.data?.latitude && item.data?.longitude) {
        onGeoChange?.(item.data);
      } else {
        onGeoChange?.();
      }
    };
    $[11] = onGeoChange;
    $[12] = onValueChange;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const handleSelect = t7;
  let t8;
  if ($[14] !== handleSelect || $[15] !== onGeoChange || $[16] !== onSubmit || $[17] !== onValueChange || $[18] !== suggestions) {
    t8 = value => {
      if (!value.trim()) {
        onValueChange?.("");
        onGeoChange?.();
        onSubmit?.("");
        setLocationSearch("");
        return;
      }
      if (suggestions.length > 0) {
        handleSelect(suggestions[0]);
        onSubmit?.(suggestions[0].value);
        return;
      }
      if (value.length >= 2) {
        fetchAutocompletePostalCode(value, 1).then(results => {
          if (results.length > 0) {
            const r_0 = results[0];
            handleSelect({
              label: formatPostalCodeLabel(r_0),
              value: r_0.postalCode,
              data: {
                city: r_0.city,
                state: r_0.state,
                latitude: r_0.lat,
                longitude: r_0.lng
              }
            });
            onSubmit?.(r_0.postalCode);
          } else {
            onValueChange?.(value);
            onGeoChange?.();
            onSubmit?.(value);
          }
        });
        return;
      }
      onValueChange?.(value);
      onSubmit?.(value);
    };
    $[14] = handleSelect;
    $[15] = onGeoChange;
    $[16] = onSubmit;
    $[17] = onValueChange;
    $[18] = suggestions;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const handleSubmit = t8;
  const t9 = handleSelect as (item: ComboboxItem) => void;
  const t10 = locationSearch.length >= 3 && !isLoading ? "No results" : undefined;
  let t11;
  if ($[20] !== placeholder || $[21] !== t) {
    t11 = placeholder ?? t("volunteers:zipcodePlaceholder");
    $[20] = placeholder;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== placeholder || $[24] !== t) {
    t12 = placeholder ?? t("volunteers:zipcodePlaceholder");
    $[23] = placeholder;
    $[24] = t;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== ariaLabel || $[27] !== t) {
    t13 = ariaLabel ?? t("volunteers:zipcodeLabel");
    $[26] = ariaLabel;
    $[27] = t;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== t) {
    t14 = t("volunteers:useMyLocation");
    $[29] = t;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== requestGeolocation || $[32] !== t14) {
    t15 = {
      label: t14,
      icon: "marker-pin-01",
      onSelect: requestGeolocation
    };
    $[31] = requestGeolocation;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== className || $[35] !== controlledValue || $[36] !== handleSubmit || $[37] !== isLoading || $[38] !== suggestions || $[39] !== t10 || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t15 || $[44] !== t9) {
    t16 = <ComboboxField value={controlledValue} onChange={setLocationSearch} onSelect={t9} onSubmit={handleSubmit} items={suggestions} isLoading={isLoading} noResultsText={t10} placeholder={t11} searchPlaceholder={t12} aria-label={t13} iconName="marker-pin-01" pattern="[0-9]*" minChars={3} headerAction={t15} className={className} />;
    $[34] = className;
    $[35] = controlledValue;
    $[36] = handleSubmit;
    $[37] = isLoading;
    $[38] = suggestions;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t15;
    $[44] = t9;
    $[45] = t16;
  } else {
    t16 = $[45];
  }
  return t16;
};
function _temp(r) {
  return {
    label: formatPostalCodeLabel(r),
    value: r.postalCode,
    data: {
      city: r.city,
      state: r.state,
      latitude: r.lat,
      longitude: r.lng
    }
  };
}